import React, { Component } from "react";
import { MDBBtn, MDBIcon, MDBRow, MDBCol } from "mdbreact";
import "./MultiTenancy.css";

/**
 * Operator Association Panel — Mockup #6
 *
 * Displays Primary & Secondary Operator info on the Venue Detail page.
 * Adapts based on whether the current viewer is the Primary or Secondary Operator.
 *
 * Props:
 *   primaryOperator      - { id, name }
 *   secondaryOperator    - { id, name, status, approvedAt, approvedBy } or null
 *   viewerRole           - "PRIMARY" | "SECONDARY"
 *   viewerPermissions    - object (used when viewerRole is "SECONDARY")
 *   onRemoveSecondary()  - called when Primary clicks Remove
 *   onViewPermissions()  - called to view configured permissions
 */
class OperatorAssociationPanel extends Component {
    constructor(props) {
        super(props);
        this.state = {
            permissionsExpanded: false,
        };
    }

    togglePermissions = () => {
        this.setState({
            permissionsExpanded: !this.state.permissionsExpanded,
        });
    };

    renderStatusBadge = (status) => {
        const map = {
            ACTIVE: { label: "Active", className: "status-active" },
            PENDING: { label: "Pending", className: "status-pending" },
            REJECTED: { label: "Rejected", className: "status-rejected" },
            REMOVED: { label: "Removed", className: "status-removed" },
            SUSPENDED: { label: "Suspended", className: "status-suspended" },
        };
        const entry = map[status] || { label: status, className: "status-none" };
        return (
            <span className={`mt-status-badge ${entry.className}`}>
                {entry.label}
            </span>
        );
    };

    renderPermSummary = (perms) => {
        if (!perms) return null;
        const entries = [
            ["View / Edit Subscriptions", perms.editSubscriptions],
            ["Create Resident Tickets", perms.createResidentTickets],
            ["Delete Subscriptions", perms.deleteSubscriptions],
            ["Edit Venue Settings", perms.editVenueSettings],
            ["Manage Admins", perms.manageAdmins],
        ];

        return (
            <div className="mt-perm-summary">
                {entries.map(([label, allowed]) => (
                    <div key={label} className="mt-permission-row">
                        <span
                            className={
                                allowed
                                    ? "mt-perm-allowed"
                                    : "mt-perm-denied"
                            }
                        >
                            {allowed ? (
                                <MDBIcon icon="check" />
                            ) : (
                                <MDBIcon icon="times" />
                            )}
                        </span>
                        <span
                            className={
                                allowed
                                    ? "mt-perm-label"
                                    : "mt-perm-label mt-perm-label-denied"
                            }
                        >
                            {label}
                        </span>
                    </div>
                ))}
            </div>
        );
    };

    renderPrimaryView = () => {
        const {
            primaryOperator,
            secondaryOperator,
            onRemoveSecondary,
            onViewPermissions,
        } = this.props;

        return (
            <>
                <MDBRow className="mt-operator-row">
                    <MDBCol md="4">
                        <span className="mt-operator-label">
                            Primary Operator
                        </span>
                    </MDBCol>
                    <MDBCol md="8">
                        <p className="mt-operator-name">
                            {primaryOperator.name}
                        </p>
                        <p className="mt-muted">{primaryOperator.id}</p>
                        <span className="mt-role-tag mt-role-owner">
                            <MDBIcon icon="crown" /> You are the Primary
                            Operator
                        </span>
                    </MDBCol>
                </MDBRow>

                <hr />

                <MDBRow className="mt-operator-row">
                    <MDBCol md="4">
                        <span className="mt-operator-label">
                            Secondary Operator
                        </span>
                    </MDBCol>
                    <MDBCol md="8">
                        {secondaryOperator ? (
                            <>
                                <p className="mt-operator-name">
                                    {secondaryOperator.name}
                                </p>
                                <p className="mt-muted">
                                    {secondaryOperator.id}
                                </p>
                                <p>
                                    <strong>Status: </strong>
                                    {this.renderStatusBadge(
                                        secondaryOperator.status
                                    )}
                                </p>
                                {secondaryOperator.approvedAt && (
                                    <p className="mt-muted">
                                        Approved:{" "}
                                        {secondaryOperator.approvedAt} by{" "}
                                        {secondaryOperator.approvedBy}
                                    </p>
                                )}
                                <div className="mt-action-row">
                                    <MDBBtn
                                        color="primary"
                                        size="sm"
                                        onClick={onViewPermissions}
                                    >
                                        <MDBIcon icon="key" /> View Permissions
                                    </MDBBtn>
                                    {secondaryOperator.status === "ACTIVE" && (
                                        <MDBBtn
                                            color="danger"
                                            size="sm"
                                            onClick={onRemoveSecondary}
                                        >
                                            <MDBIcon icon="user-minus" />{" "}
                                            Remove Secondary Operator
                                        </MDBBtn>
                                    )}
                                </div>
                            </>
                        ) : (
                            <p className="mt-muted">
                                No Secondary Operator is associated with this
                                venue.
                            </p>
                        )}
                    </MDBCol>
                </MDBRow>
            </>
        );
    };

    renderSecondaryView = () => {
        const { primaryOperator, secondaryOperator, viewerPermissions } =
            this.props;

        return (
            <>
                <MDBRow className="mt-operator-row">
                    <MDBCol md="4">
                        <span className="mt-operator-label">
                            Primary Operator
                        </span>
                    </MDBCol>
                    <MDBCol md="8">
                        <p className="mt-operator-name">
                            {primaryOperator.name}
                        </p>
                        <p className="mt-muted">{primaryOperator.id}</p>
                        <span className="mt-role-tag mt-role-owner">
                            <MDBIcon icon="crown" /> Owner
                        </span>
                    </MDBCol>
                </MDBRow>

                <hr />

                <MDBRow className="mt-operator-row">
                    <MDBCol md="4">
                        <span className="mt-operator-label">Your Role</span>
                    </MDBCol>
                    <MDBCol md="8">
                        <span className="mt-role-tag mt-role-secondary">
                            <MDBIcon icon="handshake" /> Secondary Operator
                        </span>
                        {secondaryOperator && secondaryOperator.approvedAt && (
                            <p className="mt-muted mt-spacer-top">
                                Approved: {secondaryOperator.approvedAt}
                            </p>
                        )}
                    </MDBCol>
                </MDBRow>

                <MDBRow className="mt-operator-row">
                    <MDBCol md="4">
                        <span className="mt-operator-label">
                            Your Access Level
                        </span>
                    </MDBCol>
                    <MDBCol md="8">
                        <MDBBtn
                            color="link"
                            size="sm"
                            className="mt-toggle-perms-btn"
                            onClick={this.togglePermissions}
                        >
                            {this.state.permissionsExpanded
                                ? "Hide Permissions"
                                : "View My Permissions"}{" "}
                            <MDBIcon
                                icon={
                                    this.state.permissionsExpanded
                                        ? "chevron-up"
                                        : "chevron-down"
                                }
                            />
                        </MDBBtn>
                        {this.state.permissionsExpanded &&
                            this.renderPermSummary(viewerPermissions)}
                    </MDBCol>
                </MDBRow>
            </>
        );
    };

    render() {
        const { viewerRole } = this.props;

        return (
            <div className="mt-association-panel">
                <div className="mt-section-header">
                    <MDBIcon icon="users-cog" />
                    <h5>Operator Information</h5>
                </div>

                {viewerRole === "SECONDARY"
                    ? this.renderSecondaryView()
                    : this.renderPrimaryView()}
            </div>
        );
    }
}

export default OperatorAssociationPanel;
